/**
 * TanStack Query hooks for `referral-code-tracking` (FS-826).
 *
 * Thin wrappers over `./api`. Every mutation invalidates the shared list
 * key so the KPI tiles and the table re-render with fresh numbers.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	checkAvailability,
	create,
	deactivate,
	list,
	listSignups,
	referralCodeTrackingQueryKeys,
	type CheckCodeAvailabilityResponse,
	type CreateReferralCodeBody,
	type ListReferralCodesResponse,
	type ListSignupsResponse,
	type ReferralCodeRow,
} from "./api";

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useReferralCodesList(): UseQueryResult<
	ListReferralCodesResponse,
	Error
> {
	return useQuery({
		queryKey: referralCodeTrackingQueryKeys.list(),
		queryFn: list,
	});
}

export function useReferralSignups(
	referralId: string | null,
): UseQueryResult<ListSignupsResponse, Error> {
	return useQuery({
		enabled: Boolean(referralId),
		queryKey: referralId
			? referralCodeTrackingQueryKeys.signups(referralId)
			: ["referral-code-tracking", "signups", "noop"],
		queryFn: () => listSignups(referralId as string),
	});
}

/**
 * Availability poll for vanity-code input. Query is only enabled when the
 * code is a valid shape (handled by the page); tests can bypass by
 * rendering without a code.
 */
export function useVanityCodeAvailability(
	code: string | null,
): UseQueryResult<CheckCodeAvailabilityResponse, Error> {
	return useQuery({
		enabled: Boolean(code),
		queryKey: code
			? referralCodeTrackingQueryKeys.availability(code)
			: ["referral-code-tracking", "availability", "noop"],
		queryFn: () => checkAvailability(code as string),
		staleTime: 30_000,
	});
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useReferralCodeCreate(): UseMutationResult<
	ReferralCodeRow,
	Error,
	CreateReferralCodeBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: referralCodeTrackingQueryKeys.all,
			});
		},
	});
}

export function useReferralCodeDeactivate(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deactivate,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: referralCodeTrackingQueryKeys.all,
			});
		},
	});
}
